// src/offline/downloadQueue.ts
import { downloadPrefix, DownloadController, type DownloadResult, type Prefix } from "./offlineDownload";
import { useOfflineDownloadStore } from "./offlineDownloadStore";
// import { useOfflineDownloadStore } from "./offlineDownloadStore";

type Job = {
  prefix: Prefix;
  controller: DownloadController;
  onProgress?: (p: any) => void;
  resolve: (value: DownloadResult) => void;
  reject: (err: any) => void;
};

class DownloadQueue {
  private queue: Job[] = [];
  private busy = false;
  private current: Job | null = null;

  add(prefix: Prefix, opts: { controller: DownloadController; onProgress?: (p: any) => void }) {
    return new Promise<DownloadResult>((resolve, reject) => {
      this.queue.push({ prefix, ...opts, resolve, reject });
      useOfflineDownloadStore.getState().setJob(prefix, { status: "queued" });
      void this.runNext();
    });
  }

  clearWaiting() {
    this.queue = [];
  }

  cancelAll() {
    this.clearWaiting();
    if (this.current?.controller) {
      this.current.controller.cancel();
    }
  }

  private async runNext(): Promise<void> {
    if (this.busy) return;
    const job = this.queue.shift();
    if (!job) return;

    const { prefix, controller, onProgress, resolve, reject } = job;
    const store = useOfflineDownloadStore.getState();

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      store.setJob(prefix, { status: "can_resume", controller: null });
      resolve({ ok: false, paused: true, error: "offline" });
      return this.runNext();
    }

    this.busy = true;
    this.current = job;

    try {
      store.setJob(prefix, { status: "starting", controller });

      const res: DownloadResult = await downloadPrefix(prefix, {
        controller,
        onProgress: (p) => {
          const currentStatus = useOfflineDownloadStore.getState().jobs[prefix]?.status;
          const status = currentStatus === "cancelling" ? "cancelling" : "downloading";
          store.setJob(prefix, { status, controller, progress: p });
          onProgress?.(p);
        },
      });

      resolve(res);
    } catch (err) {
      reject(err);
    } finally {
      this.busy = false;
      this.current = null;
      await this.runNext();
    }
  }
}

export const downloadQueue = new DownloadQueue();
